import { useEffect, type ReactNode } from 'react';
import { useNav } from '../navigation.js';
import { usePermissions } from '../permissions.js';
import { SettingsHeader } from '../settings/common.js';
import { UNSAVED_PROMPT } from './copy.js';

/**
 * The Location & geofencing hub shell: one header, one sub-navigation, and the
 * active section beneath it (design doc "Decided architecture" #1, modelled on
 * the analytics-shell strip — but these are ROUTES, not tabs-in-place, so the
 * strip is a `<nav>` of real links with `aria-current`, which is the accessible
 * shape for navigation between pages).
 *
 * ONLY SECTIONS THAT ROUTE SOMEWHERE REAL ARE LISTED (directive §32 — no
 * decorative surfaces, same rule settingsNav.ts states for the whole Settings
 * centre). Work Sites and Geofences joined with L116; Clock Rules and
 * Exceptions with L117; Activity with L118. Two of the design doc's named
 * sections are deliberately NOT separate entries, and each absence is a
 * decision:
 *
 *   - ISSUES is folded into Exceptions. The doc's decided architecture #4
 *     says the exception table IS the V1 issue feed, and its own hub list
 *     (#1) names Exceptions with no separate Issues; the two derivable
 *     detections render as a card on the Exceptions page — same facts, same
 *     readers, same key.
 *   - ALERTS has no hub section because the two location alert rule types
 *     live in the EXISTING rules engine and are configured where every other
 *     automation is — Settings → Rules & Automations (doc #5: "no duplicate
 *     notification machinery", which includes no duplicate configuration
 *     surface). A second editor for the same workflow_rule rows would be the
 *     decorative-control failure §32 exists to prevent.
 *
 * No dead links, no "coming soon" stubs.
 *
 * A section may carry a permission BEYOND the hub's own `location:read` gate
 * when the server route behind it demands more: `GET /location/geofences` is
 * `location:manage` (the whole geofence surface is configuration), so listing
 * Geofences for a read-only holder would be a link that can only 403 — it is
 * absent instead, exactly like a settingsNav item the principal cannot read.
 *
 * The shell also owns unsaved-changes protection in one place: the page passes
 * its `dirty` flag, and a dirty page (a) arms the browser's beforeunload
 * prompt and (b) asks before in-hub navigation discards the edits.
 */

export type LocationSectionKey =
  | 'general'
  | 'tracking'
  | 'sites'
  | 'geofences'
  | 'clock-rules'
  | 'exceptions'
  | 'privacy'
  | 'activity';

/**
 * `permission` is what a section's ROUTE gates on. Every hub page reads with
 * `location:read`; the Exceptions queue is worker time data, so it asks the
 * API's own read key (`time:read`) instead — and a principal without it is not
 * shown a link that would 403.
 */
export const LOCATION_SECTIONS: readonly {
  key: LocationSectionKey;
  label: string;
  href: string;
  permission: string;
}[] = [
  { key: 'general', label: 'General', href: '/settings/location', permission: 'location:read' },
  {
    key: 'tracking',
    label: 'Location tracking',
    href: '/settings/location/tracking',
    permission: 'location:read',
  },
  {
    key: 'sites',
    label: 'Work sites',
    href: '/settings/location/sites',
    permission: 'location:read',
  },
  {
    key: 'geofences',
    label: 'Geofences',
    href: '/settings/location/geofences',
    permission: 'location:manage',
  },
  {
    key: 'clock-rules',
    label: 'Clock rules',
    href: '/settings/location/clock-rules',
    permission: 'location:read',
  },
  {
    key: 'exceptions',
    label: 'Exceptions',
    href: '/settings/location/exceptions',
    permission: 'time:read',
  },
  {
    key: 'privacy',
    label: 'Privacy & retention',
    href: '/settings/location/privacy',
    permission: 'location:read',
  },
  // Activity is the audit centre's own record filtered to location, so it
  // charges the audit centre's own key — `audit:read` (org_admin only).
  {
    key: 'activity',
    label: 'Activity',
    href: '/settings/location/activity',
    permission: 'audit:read',
  },
];

export function LocationSettingsShell({
  active,
  dirty = false,
  children,
}: {
  active: LocationSectionKey;
  /** True while the page beneath holds unsaved edits. */
  dirty?: boolean;
  children: ReactNode;
}) {
  const navigate = useNav();
  const { has } = usePermissions();
  const sections = LOCATION_SECTIONS.filter((section) => has(section.permission));

  // Armed only while there are genuinely unsaved edits, so it never nags
  // someone who changed nothing (same shape as OrgSettings).
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  const go = (href: string, isActive: boolean) => {
    if (isActive) return;
    if (dirty && !window.confirm(UNSAVED_PROMPT)) return;
    navigate(href);
  };

  return (
    <div className="rn-col" style={{ maxWidth: 760 }}>
      <SettingsHeader
        title="Location & geofencing"
        blurb="Whether location is collected on shifts, what it is checked against, and how long it is kept."
      />
      <nav className="rn-tabs" aria-label="Location & geofencing sections">
        {sections.map((section) => {
          const isActive = section.key === active;
          return (
            <a
              key={section.key}
              className="rn-tab"
              href={section.href}
              aria-current={isActive ? 'page' : undefined}
              // `.rn-tab` keys its selected look off aria-selected/aria-pressed,
              // neither of which is valid on a link; the same tokens are applied
              // inline so the active section looks selected without inventing an
              // invalid ARIA state.
              style={
                isActive
                  ? {
                      background: 'var(--color-surface)',
                      color: 'var(--color-navy)',
                      boxShadow: 'var(--shadow-raised)',
                      textDecoration: 'none',
                    }
                  : { textDecoration: 'none' }
              }
              onClick={(e) => {
                e.preventDefault();
                go(section.href, isActive);
              }}
            >
              {section.label}
            </a>
          );
        })}
      </nav>
      {children}
    </div>
  );
}
